import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import Postcard from "@/components/Postcard";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading, refresh, updateUser } = useAuth();
  const [state, setState] = useState({ status: "checking", invite: null, error: null });
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then((r) => setState({ status: "valid", invite: r.data, error: null }))
      .catch((err) => setState({ status: "invalid", invite: null, error: err.response?.data?.detail || "Invalid invite link" }));
  }, [token]);

  const handleRedeem = async () => {
    if (!user) {
      navigate(`/auth?mode=signup&next=/invite/${token}`);
      return;
    }
    setRedeeming(true);
    try {
      const { data } = await api.post(`/invites/${token}/redeem`);
      // Seed the AuthContext with the upgraded user synchronously so ProtectedRoute sees is_seller=true on next render.
      if (data?.user) updateUser(data.user);
      else await refresh();
      toast.success("You're a Nirno creator now.");
      navigate("/seller", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not redeem invite");
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--ink)] text-white grid lg:grid-cols-2" data-testid="invite-page">
      <div className="p-12 flex flex-col justify-between">
        <Logo to="/" inverse size={32} />
        <div>
          <span className="eyebrow" style={{ color: "rgba(255,255,255,0.6)" }}>◆ Nirno seller program</span>
          <h2 className="h-display text-[42px] sm:text-[52px] mt-4 leading-[0.95] text-white max-w-[460px]">
            Your trips are worth more than a carousel.
          </h2>
          <p className="mt-6 text-[15px] leading-[1.7] text-[#cbb9a8] max-w-[440px]">
            Nirno is invite-only. Founders personally vet every creator. Welcome — you've made it through.
          </p>

          <div className="mt-10">
            <span
              className="mono text-[11px] tracking-[0.2em] uppercase font-semibold"
              style={{ color: "#E63946" }}
              data-testid="invite-eyebrow"
            >
              ◆ Creator invite
            </span>
            <p className="mt-2 text-[16px] leading-[1.55] text-white max-w-[420px]">
              You've been invited to sell on Nirno.
            </p>
            <div className="mt-6 max-w-[360px]">
              <Postcard />
            </div>
          </div>
        </div>
        <div className="mono text-[10px] tracking-[0.2em] uppercase text-[#a89684]">
          You keep the full sale price · No commission taken
        </div>
      </div>

      <div className="bg-[var(--paper)] text-[var(--ink)] p-12 flex items-center">
        <div className="w-full max-w-[420px] mx-auto">
          {state.status === "checking" && (
            <div className="mono text-[11px] tracking-[0.2em] uppercase text-[var(--ink3)]">◆ Checking invite…</div>
          )}

          {state.status === "invalid" && (
            <div data-testid="invite-invalid">
              <span className="eyebrow">◆ Invite issue</span>
              <h2 className="h-display text-[34px] mt-3 leading-[1.05]">
                This invite isn't valid.
              </h2>
              <p className="mt-4 text-[14px] text-[var(--ink2)] leading-[1.65]">
                {state.error}. If you believe this is a mistake, reply to your invite email or reach out at <strong>creators@nirno.com</strong>.
              </p>
              <button onClick={() => navigate("/")} className="btn btn-ghost mt-6">← Back to home</button>
            </div>
          )}

          {state.status === "valid" && (
            <div data-testid="invite-valid">
              <span className="eyebrow">◆ You're invited</span>
              <h2 className="h-display text-[36px] mt-3 leading-[1]">Welcome, creator.</h2>
              <p className="mt-4 text-[14px] text-[var(--ink2)] leading-[1.65]">
                This invite was sent to <strong>{state.invite.email}</strong>.
                Activating it upgrades your account to a Nirno creator account so you can publish itineraries.
              </p>

              <div className="bg-white border-[1.5px] border-[var(--line)] rounded-[14px] p-4 mt-6">
                <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink3)] mb-1">Invite expires</div>
                <div className="text-[14px] font-bold">
                  {new Date(state.invite.expires_at).toLocaleString()}
                </div>
              </div>

              {!loading && !user && (
                <p className="mt-5 text-[13px] text-[var(--ink3)]">
                  You'll need an account first — we'll bring you back here after sign in.
                </p>
              )}

              <button
                onClick={handleRedeem}
                disabled={redeeming}
                data-testid="invite-redeem"
                className="btn btn-solid btn-lg full mt-6"
              >
                {redeeming ? "…" : !user ? "Sign in & activate →" : "Activate seller account →"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
